// PrimeReact Dropdown: onChange(e) => e.value (the option's value field, typed as any).
// Verified from existing usages:
//   - apps/web/src/modules/reports/ComplianceReports.tsx:40 — onChange={e => setReportType(String(e.value))}
//   - apps/web/src/modules/reports/HistoricalReports.tsx:85 — onChange={(e) => setReportType(e.value as ReportType)}
//   - apps/web/src/pages/agent/DevicesPage.tsx:223-224 — onChange={(e) => setForm({ ...form, deviceType: e.value as DeviceType })}
// PrimeReact Dropdown uses `inputId` (not `id`) to set the id on the inner <input>.
// Verified at apps/web/node_modules/primereact/dropdown/dropdown.d.ts:389 — inputId?: string

import { useId, type JSX } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Dropdown } from 'primereact/dropdown'

interface DropdownOption {
  label: string
  value: string | number
}

interface Props {
  name: string
  label: string
  options: DropdownOption[]
  helpText?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  filter?: boolean
}

export function FormDropdown({
  name,
  label,
  options,
  helpText,
  placeholder,
  required,
  disabled,
  filter,
}: Props): JSX.Element {
  const {
    control,
    formState: { errors },
  } = useFormContext()
  const error = errors[name]
  const errorMessage = typeof error?.message === 'string' ? error.message : undefined
  const id = useId()
  const errorId = `${id}-error`
  const helpId = `${id}-help`
  const describedBy =
    [helpText && helpId, errorMessage && errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="flex flex-col gap-1.5 mb-4">
      <label htmlFor={id} className="text-sm font-medium text-gray-700">
        {label}
        {required && <span aria-hidden className="text-red-500 ml-1">*</span>}
      </label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Dropdown
            inputId={id}
            value={field.value}
            onChange={(e) => field.onChange(e.value)}
            onBlur={field.onBlur}
            options={options}
            {...(placeholder !== undefined ? { placeholder } : {})}
            {...(disabled !== undefined ? { disabled } : {})}
            {...(filter !== undefined ? { filter } : {})}
            aria-invalid={error ? 'true' : 'false'}
            {...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {})}
            // `input` here is the Dropdown's *label* span, which already sits
            // inside the bordered `.p-dropdown` root (styled globally in
            // styles/primereact-overrides.css). Giving it its own border/bg
            // draws a second box inside the first — so it only carries type
            // styling and lets the root own the frame.
            pt={{
              root: { className: 'w-full' },
              input: { className: 'w-full bg-transparent text-sm text-gray-900' },
              panel: { className: 'mt-1 rounded-lg border border-gray-200 shadow-lg' },
              item: { className: 'py-2.5 px-3 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer' },
              list: { className: 'py-1' }
            } as any}
          />
        )}
      />
      {helpText && (
        <small id={helpId} className="text-gray-500 text-xs">
          {helpText}
        </small>
      )}
      {errorMessage && (
        <small id={errorId} role="alert" className="text-red-600 text-xs font-medium">
          {errorMessage}
        </small>
      )}
    </div>
  )
}
